import React, { createContext, useContext, useMemo, useState } from 'react';

interface AuthContextValue {
	token: string | null;
	isAuthenticated: boolean;
	isInitializing: boolean;
	setToken: (token: string | null) => void;
	setIsInitializing: (initializing: boolean) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
	children: React.ReactNode;
	initialToken?: string | null;
	initialIsInitializing?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, initialToken = null, initialIsInitializing = true }) => {
	const [token, setToken] = useState<string | null>(initialToken);
	const [isInitializing, setIsInitializing] = useState<boolean>(initialIsInitializing);

	const logout = () => setToken(null);

	const value = useMemo(
		() => ({ token, isAuthenticated: !!token, isInitializing, setToken, setIsInitializing, logout }),
		[token, isInitializing]
	);

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = (): AuthContextValue => {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return ctx;
}; 